import React from 'react';
import { Mafs, Theme, Polygon, Text } from 'mafs';

export const VisualMultiplier = ({ config = {} }) => {
    /**
     * Backend alignment notes (VisualMultiplierValidator):
     * - Dimensions: NaN/Infinity => 0.0, negatives clamped to 0.0
     * - If config is null, backend uses 1,1,1,1 with labels A/B/C/D
     * - Labels: if labels object exists, null fields become "" (empty string)
     */

    const sanitizeDim = (val, defaultWhenMissing = 1) => {
        // Missing (undefined/null) => fallback (matches backend null-config default intent)
        if (val === undefined || val === null) return defaultWhenMissing;

        const n = Number(val);
        // Invalid number => 0 (matches backend validator sanitizeDim)
        if (!Number.isFinite(n)) return 0;
        return Math.max(0, n);
    };

    const labelFor = (key, fallbackLetter) => {
        // If labels object (or property) is missing, show fallback letter
        const labels = config.labels;
        if (labels === undefined || labels === null) return fallbackLetter;

        const v = labels[key];
        // Explicit null => empty string (matches backend validator behaviour for null labels)
        if (v === null) return '';

        // Undefined (missing key) => fallback letter; otherwise keep string (including "")
        return v === undefined ? fallbackLetter : v;
    };

    // 1) Extract data (safe + backend-aligned sanitisation)
    const A = sanitizeDim(config.valueA, 1);
    const B = sanitizeDim(config.valueB, 1);
    const C = sanitizeDim(config.valueC, 1);
    const D = sanitizeDim(config.valueD, 1);

    // 2) Labels (preserve empty string; only default when missing)
    const lA = labelFor('A', 'A');
    const lB = labelFor('B', 'B');
    const lC = labelFor('C', 'C');
    const lD = labelFor('D', 'D');

    const scale = 1.0;
    const wA = A * scale;
    const wB = B * scale;
    const hC = C * scale;
    const hD = D * scale;

    const totalW = wA + wB;
    const totalH = hC + hD;

    // Center diagram
    const startX = -totalW / 2;
    const startY = -totalH / 2;

    const RectRegion = ({ x, y, w, h, color, label }) => {
        if (w <= 0 || h <= 0) return null;

        return (
            <g>
                <Polygon
                    points={[[x, y], [x + w, y], [x + w, y + h], [x, y + h]]}
                    color={color}
                    fillOpacity={0.2}
                    // Mafs styling: use svgPolygonProps for explicit SVG stroke attributes
                    svgPolygonProps={{
                        stroke: 'black',
                        strokeWidth: 1,
                    }}
                />
                <Text x={x + w / 2} y={y + h / 2} size={16} color="black">
                    {label}
                </Text>
            </g>
        );
    };

    // Padding around content so labels don’t clip
    const pad = 1;

    return (
        <Mafs
            viewBox={{
                x: [startX - pad, startX + totalW + pad],
                y: [startY - pad, startY + totalH + pad],
            }}
            pan={false}
            preserveAspectRatio={false}
        >
            <g>
                {/*
                  FIXED Y-ORDER: Top-to-Bottom Reading
                  Mafs Y is UP.
                  Top Row (C) starts at: startY + hD (Bottom D pushes C up)
                  Bottom Row (D) starts at: startY
                */}

                {/* --- TOP ROW (Height C) --- */}
                {/* Top Left: A * C */}
                <RectRegion
                    x={startX}
                    y={startY + hD}
                    w={wA}
                    h={hC}
                    color={Theme.blue}
                    label={`${lA}·${lC}`}
                />
                {/* Top Right: B * C */}
                <RectRegion
                    x={startX + wA}
                    y={startY + hD}
                    w={wB}
                    h={hC}
                    color={Theme.red}
                    label={`${lB}·${lC}`}
                />

                {/* --- BOTTOM ROW (Height D) --- */}
                {/* Bottom Left: A * D */}
                <RectRegion
                    x={startX}
                    y={startY}
                    w={wA}
                    h={hD}
                    color={Theme.green}
                    label={`${lA}·${lD}`}
                />
                {/* Bottom Right: B * D */}
                <RectRegion
                    x={startX + wA}
                    y={startY}
                    w={wB}
                    h={hD}
                    color={Theme.orange}
                    label={`${lB}·${lD}`}
                />

                {/* --- AXIS LABELS --- */}
                {/* Top Labels (A, B) */}
                {wA > 0 && (
                    <Text x={startX + wA / 2} y={startY + totalH + 0.5} size={14}>
                        {`${lA} (${A})`}
                    </Text>
                )}
                {wB > 0 && (
                    <Text x={startX + wA + wB / 2} y={startY + totalH + 0.5} size={14}>
                        {`${lB} (${B})`}
                    </Text>
                )}

                {/* Side Labels (C, D) */}
                {hC > 0 && (
                    <Text x={startX - 0.5} y={startY + hD + hC / 2} attach="e" size={14}>
                        {`${lC} (${C})`}
                    </Text>
                )}
                {hD > 0 && (
                    <Text x={startX - 0.5} y={startY + hD / 2} attach="e" size={14}>
                        {`${lD} (${D})`}
                    </Text>
                )}
            </g>
        </Mafs>
    );
};
